import React, { useCallback, useEffect, useRef, useState, createContext, useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { useNavigate } from 'react-router-dom';
import { overAllCostTree, workspaceGetEnv, fetchSectorList } from 'service/service';
import moment from 'moment';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import LoaderComponent from 'components/Loader';
import FiltersBar from './FilterBar';
import { extractDatesFromNode, findSelectedNode } from 'utils/common-util';
import { commonFetchSectors, useAdbFilters } from 'utils/adb-common-util';
import PageWrapper from 'components/Common/PageWraper';

const columnMapping: Record<string, string> = {
    time_range: 'Time Range',
    cost: 'Cost',
    percent_of_total: '% of Total'
};

const defaultColumnNames = ['Time Range', 'Cost', '% of Total'];

const regions = [
    'ALL',
    'Brazil South',
    'Australia East',
    'UK South',
    'South Central US',
    'East US',
    'Germany West Central'
];
const regionOptions = regions.map(location => ({ label: location, value: location }));

const sectorOptionsList = [
    'latam', 'apac', 'TBD', 'amesa', 'essa', 'flna', 'global', 'tbd', 'pbna', 'pfna', 'snt'
];

// Flatten cost data structure
function flattenCostData(data: any[], totalCost: number): any[] {
    const rows: any[] = [];

    function calculatePercentage(value: number): string {
        if (!totalCost || totalCost === 0) return '0.00';
        return ((value / totalCost) * 100).toFixed(2);
    }

    function addRows(obj: any, level: number, parentId: string) {
        // Check for YEAR level
        if (obj.year) {
            const id = parentId ? `${parentId}-year-${obj.year.year_name}` : `year-${obj.year.year_name}`;
            const costValue = obj.year.cost || 0;
            rows.push({
                id,
                time_range: obj.year.year_name,
                cost: costValue,
                percent_of_total: calculatePercentage(costValue),
                children: obj.year.children || [],
                isExpanded: false,
                level
            });
        }
        // Check for MONTH level
        else if (obj.month) {
            const id = parentId ? `${parentId}-month-${obj.month.month_name}` : `month-${obj.month.month_name}`;
            const costValue = obj.month.cost || 0;
            rows.push({
                id,
                time_range: obj.month.month_name,
                cost: costValue,
                percent_of_total: calculatePercentage(costValue),
                children: obj.month.children || [],
                isExpanded: false,
                level
            });
        }
        // Check for WEEK level
        else if (obj.week) {
            const id = parentId ? `${parentId}-week-${obj.week.week_name}` : `week-${obj.week.week_name}`;
            const costValue = obj.week.cost || 0;
            rows.push({
                id,
                time_range: obj.week.week_name,
                cost: costValue,
                percent_of_total: calculatePercentage(costValue),
                children: obj.week.children || [],
                isExpanded: false,
                level
            });
        }
        // Check for DAY level
        else if (obj.day) {
            const id = parentId ? `${parentId}-day-${obj.day.day_name}` : `day-${obj.day.day_name}`;
            const costValue = obj.day.cost || 0;
            rows.push({
                id,
                time_range: obj.day.day_name,
                cost: costValue,
                percent_of_total: calculatePercentage(costValue),
                date: obj.day.date,
                children: [],
                isExpanded: false,
                level
            });
        }
    }

    data.forEach((obj: any) => {
        console.log("Flattening object:", obj);
        addRows(obj, 0, '');
    });

    return rows;
}

// Get immediate children for expansion
function getImmediateChildren(row: any, totalCost: number): any[] {
    const childrenRows: any[] = [];

    function calculatePercentage(value: number): string {
        if (!totalCost || totalCost === 0) return '0.00';
        return ((value / totalCost) * 100).toFixed(2);
    }

    row.children.forEach((obj: any) => {
        // Check for month (child of year)
        if (obj.month) {
            const costValue = obj.month.cost || 0;
            childrenRows.push({
                id: `${row.id}-month-${obj.month.month_name}`,
                time_range: obj.month.month_name,
                cost: costValue,
                percent_of_total: calculatePercentage(costValue),
                children: obj.month.children || [],
                isExpanded: false,
                level: row.level + 1
            });
        }
        // Check for week
        else if (obj.week) {
            const costValue = obj.week.cost || 0;
            childrenRows.push({
                id: `${row.id}-week-${obj.week.week_name}`,
                time_range: obj.week.week_name,
                cost: costValue,
                percent_of_total: calculatePercentage(costValue),
                children: obj.week.children || [],
                isExpanded: false,
                level: row.level + 1
            });
        }
        // Check for day
        else if (obj.day) {
            const costValue = obj.day.cost || 0;
            childrenRows.push({
                id: `${row.id}-day-${obj.day.day_name}`,
                time_range: obj.day.day_name,
                cost: costValue,
                percent_of_total: calculatePercentage(costValue),
                date: obj.day.date,
                children: [],
                isExpanded: false,
                level: row.level + 1
            });
        }
        else {
            // Fallback for dynamic keys (day1, day2, etc.)
            const key = Object.keys(obj)[0];
            const item = obj[key];
            const costValue = item?.cost || 0;

            if (item?.week_name) {
                childrenRows.push({
                    id: `${row.id}-week-${item.week_name}`,
                    time_range: item.week_name,
                    cost: costValue,
                    percent_of_total: calculatePercentage(costValue),
                    children: item.children || [],
                    isExpanded: false,
                    level: row.level + 1
                });
            } else if (item?.day_name) {
                childrenRows.push({
                    id: `${row.id}-day-${item.day_name}`,
                    time_range: item.day_name,
                    cost: costValue,
                    percent_of_total: calculatePercentage(costValue),
                    date: item.date,
                    children: [],
                    isExpanded: false,
                    level: row.level + 1
                });
            }
        }
    });

    return childrenRows;
}
// Collapse all descendants of a row
function collapseDescendants(row: any, rows: any[], parentIdx: number) {
    let removeCount = 0;
    for (let i = parentIdx + 1; i < rows.length; i++) {
        if (rows[i].level > row.level) removeCount++;
        else break;
    }
    rows.splice(parentIdx + 1, removeCount);
    rows[parentIdx] = { ...row, isExpanded: false };
    return rows;
}

function expandRow(row: any, rows: any[], parentIdx: number, totalCost: number) {
    const childrenRows = getImmediateChildren(row, totalCost);
    rows.splice(parentIdx + 1, 0, ...childrenRows);
    rows[parentIdx] = { ...row, isExpanded: true };
    return rows;
}

function computeDateRangeFromFound(found: any, type: 'year' | 'month' | 'week' | 'day') {
    if (!found?.node) return null;
    // day
    if (type === 'day') {
        const d = found.node.date || found.node.jobRunDate;
        if (!d) return null;
        const formatted = moment(d).format('YYYY-MM-DD');
        return { startDate: formatted, endDate: formatted };
    }

    // week (children are day wrappers)
    if (type === 'week') {
        const wk = found.node;
        const dates: string[] = [];
        (wk.children || []).forEach((dayWrapper: any) => {
            const key = Object.keys(dayWrapper)[0];
            const day = dayWrapper[key];
            if (day?.date) dates.push(day.date);
            else if (day?.jobRunDate) dates.push(day.jobRunDate);
        });
        if (!dates.length) return null;
        const sorted = dates.map(d => moment(d)).sort((a, b) => a.valueOf() - b.valueOf());
        return { startDate: sorted.at(0)!.format('YYYY-MM-DD'), endDate: sorted.at(-1)!.format('YYYY-MM-DD') };
    }

    // month (children are week wrappers -> each week has children day wrappers)
    if (type === 'month') {
        const month = found.node;
        const dates: string[] = [];
        (month.children || []).forEach((weekWrapper: any) => {
            const wkKey = Object.keys(weekWrapper)[0];
            const wk = weekWrapper[wkKey];
            (wk.children || []).forEach((dayWrapper: any) => {
                const dKey = Object.keys(dayWrapper)[0];
                const d = dayWrapper[dKey];
                if (d?.date) dates.push(d.date);
                else if (d?.jobRunDate) dates.push(d.jobRunDate);
            });
        });
        if (!dates.length) return null;
        const sorted = dates.map(d => moment(d)).sort((a, b) => a.valueOf() - b.valueOf());
        return { startDate: sorted.at(0)!.format('YYYY-MM-DD'), endDate: sorted.at(-1)!.format('YYYY-MM-DD') };
    }

    return null;
}

// Context for Formatters
interface OverallCostContextType {
    toggleExpandRow: (id: string) => void;
    handleRowClick: (row: any) => void;
}
const OverallCostContext = createContext<OverallCostContextType>({
    toggleExpandRow: () => { },
    handleRowClick: () => { }
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2
});

const TimeRangeFormatter = ({ row, isCellSelected }: any) => {
    const { toggleExpandRow } = useContext(OverallCostContext);
    const hasChildren = row.children && row.children.length > 0;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', pl: row.level * 2 }}>
            {hasChildren && (
                <CellExpanderFormatter
                    isCellSelected={isCellSelected}
                    expanded={row.isExpanded}
                    onCellExpand={() => {
                        toggleExpandRow(row.id);
                    }}
                />
            )}
            <span>{row.time_range}</span>
        </Box>
    );
};

const CostFormatter = ({ row }: any) => {
    const { handleRowClick } = useContext(OverallCostContext);
    const raw = row?.cost;
    const num = typeof raw === 'string' ? Number(raw) : raw;
    const display = (typeof num === 'number' && Number.isFinite(num))
        ? currencyFormatter.format(num)
        : (raw ?? '--');
    return (
        <Box
            sx={{ cursor: 'pointer', color: '#4fc3f7' }}
            onClick={() => handleRowClick(row)}
        >
            {display}
        </Box>
    );
};

const PercentFormatter = ({ row }: any) => {
    return (
        <Box>
            {row.percent_of_total !== undefined && row.percent_of_total !== null && row.percent_of_total !== ''
                ? `${row.percent_of_total}%`
                : ''
            }
        </Box>
    );
};

const columnsDef = [
    {
        key: 'time_range',
        name: 'Time Range',
        formatter: TimeRangeFormatter
    },
    {
        key: 'cost',
        name: 'Cost',
        formatter: CostFormatter
    },
    {
        key: 'percent_of_total',
        name: '% of Total',
        formatter: PercentFormatter
    }
];

const OverallCostTablePage = () => {
    const navigate = useNavigate();
    const costExpandList = useRef<any[]>([]);
    const [tableData, setTableData] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paginationData, setPaginationData] = useState({
        totalElements: 0,
        totalPages: 0
    });
    const [loader, setLoader] = useState(false);
    const [apiResponseData, setApiResponseData] = useState<any>(null);
    const [totalCost, setTotalCost] = useState<number>(0);
    const [sectorList, setSectorList] = useState<string[]>([]);

    // Get navigation state
    const PRESERVE_FILTER_KEY = 'overall_cost_filters';
    const {
        startDate, setStartDate,
        endDate, setEndDate,
        env, setEnv,
        sector, setSector,
        sectorName, setSectorName,
        location, setLocation,
        selectedDateInString, setSelectedDateInString,
        apiDateRangeKey,
        envRef,
        restoredRef,
        initialEnvAppliedRef,
        updateHistoryState,
        handleDateSelect,
        navigationState
    } = useAdbFilters(PRESERVE_FILTER_KEY);

    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);


    useEffect(() => {
        commonFetchSectors(fetchSectorList, setSectorList);
    }, []);

    const getEnvFetch = async (location: string) => {
        try {

            const res = await workspaceGetEnv(location);
            const options = [{ label: 'ALL', value: 'both' }, ...(res?.data ?? [])];
            setEnvOptions(options);

            if (!initialEnvAppliedRef.current && navigationState?.env) {
                setEnv(navigationState.env);
                initialEnvAppliedRef.current = true;
            } else {
                const availableValues = options.map(o => o.value);
                if (!availableValues.includes(envRef.current)) {
                    setEnv(availableValues.includes('both') ? 'both' : (availableValues.at(0) ?? 'both'));
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getEnvFetch(location);
    }, [location]);

    useEffect(() => {
        const fetchData = async () => {
            setLoader(true);
            try {
                const resp = await overAllCostTree({
                    startDate,
                    endDate,
                    env,
                    sector,
                    location,
                    selectedDateInString: apiDateRangeKey,
                    page,
                    limit: pageSize
                });
                let data: any[] = [];
                let calculatedTotalCost = 0;
                if (resp?.success && Array.isArray(resp.data)) {
                    setApiResponseData(resp.data);
                    const firstItem = resp.data[0];

                    if (firstItem?.year) {
                        calculatedTotalCost = firstItem.year.cost || 0;
                    } else if (firstItem?.month) {
                        calculatedTotalCost = resp.data.reduce((sum: number, item: any) =>
                            sum + (item.month?.cost || 0), 0
                        );
                    } else if (firstItem?.week) {
                        calculatedTotalCost = resp.data.reduce((sum: number, item: any) =>
                            sum + (item.week?.cost || 0), 0
                        );
                    } else if (firstItem?.day) {
                        calculatedTotalCost = resp.data.reduce((sum: number, item: any) =>
                            sum + (item.day?.cost || 0), 0
                        );
                    } else {
                        console.error(firstItem);
                    }
                    setTotalCost(calculatedTotalCost);
                    data = flattenCostData(resp.data, calculatedTotalCost);
                }
                setTableData(data);
                costExpandList.current = data;
                setPaginationData({
                    totalElements: data.length,
                    totalPages: Math.ceil(data.length / pageSize)
                });
                setLoader(false);
            } catch (error) {
                console.error('Error fetching overall cost data:', error);
                setLoader(false);
            } finally {
                setLoader(false);
                if (restoredRef.current) {
                    sessionStorage.removeItem(PRESERVE_FILTER_KEY);
                    restoredRef.current = false;
                }
            }
        };
        fetchData();
    }, [startDate, endDate, env, sector, location, apiDateRangeKey, page, pageSize, selectedDateInString, navigationState?.startDate]);

    const toggleExpandRow = useCallback((rowId: string) => {
        const rows = costExpandList.current;
        const idx = rows.findIndex((r: any) => r.id === rowId);
        if (idx === -1) return;
        const row = rows[idx];
        let newRows = [...rows];
        if (row.isExpanded) {
            newRows = collapseDescendants(row, newRows, idx);
        } else {
            newRows = expandRow(row, newRows, idx, totalCost);
        }
        costExpandList.current = newRows;
        setTableData([...newRows]);
    }, [totalCost]);



    const handleRowClick = useCallback((row: any) => {
        let type: 'year' | 'month' | 'week' | 'day' = 'year';
        if (row.id.includes('day')) type = 'day';
        else if (row.id.includes('week')) type = 'week';
        else if (row.id.includes('month')) type = 'month';

        const found = findSelectedNode(apiResponseData, type, row.time_range, row.id);
        const computedRange = computeDateRangeFromFound(apiResponseData, type);

        // If found, send the node; else fallback to full apiResponseData
        const payload = found ?? { kind: 'full', node: apiResponseData };

        const navStateBase: any = {
            dateLabel: row.time_range,
            apiData: payload,
            type,
            startDate: computedRange?.startDate || startDate,
            endDate: computedRange?.endDate || endDate,
            env,
            sector,
            location,
            selectedDateInString
        };

        try {
            if (found?.kind) {
                const node = found.node;
                const dates = extractDatesFromNode(node);

                if (found.kind === 'day' && node?.date) {
                    const d = moment(node.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
                    setPage(1);
                    navStateBase.startDate = d;
                    navStateBase.endDate = d;
                    navStateBase.selectedDateInString = 'customDateRange';
                } else if (dates.length > 0) {
                    const moments = dates.map((ds: string) => moment(ds, 'YYYY-MM-DD'));
                    const min = moment.min(moments).format('YYYY-MM-DD');
                    const max = moment.max(moments).format('YYYY-MM-DD');
                    setPage(1);
                    navStateBase.startDate = min;
                    navStateBase.endDate = max;
                    navStateBase.selectedDateInString = 'customDateRange';
                }
            }
            const snap = {
                startDate,
                endDate,
                selectedDateInString,
            };
            sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        } catch (err) {
            console.error('Failed to derive date range', err);
        }

        navigate('/databricksADB/overall-cost/details', { state: navStateBase });
    }, [navigate, apiResponseData, startDate, endDate, env, sector, location, selectedDateInString]);




    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    const updateHistoryStateLocal = (overrides: Partial<{
        startDate: string;
        endDate: string;
        env: string;
        sector: string;
        location: string;
        selectedDateInString: string;
    }>) => {
        updateHistoryState(overrides);
    };

    const contextValue = useMemo(() => ({ toggleExpandRow, handleRowClick }), [toggleExpandRow, handleRowClick]);

    return (
        <OverallCostContext.Provider value={contextValue}>
            <PageWrapper>
                <PageHeader
                    title={[
                        {
                            text: 'Workspace Overview',
                            link: '/databricksADB/workspace',
                            state: {
                                startDate,
                                endDate,
                                env,
                                sector,
                                location,
                                selectedDateInString
                            }
                        },
                        {
                        text: 'Overall Cost'
                        }
                    ]}
                    isPepwise2Breadcrumb={true}
                />

                <FiltersBar
                    location={location}
                    // pass the actual useState setters (not single-arg wrappers)
                    setLocation={setLocation}
                    env={env}
                    setEnv={setEnv}
                    envOptions={envOptions}
                    setEnvOptions={setEnvOptions}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    handleDateSelect={handleDateSelect}
                    selectedDateInString={selectedDateInString}
                    setSelectedDateInString={setSelectedDateInString}
                    sectorName={sectorName}
                    // typed wrapper to handle SetStateAction<string> (function or value)
                    setSectorName={(v) => {
                        const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
                        setSectorName(resolved);
                        setSector(resolved);
                        setEnv('both');
                    }}
                    updateHistoryState={updateHistoryStateLocal}
                    sectorOptionsList={sectorList}
                    useHeaderFilters
                />
                {loader ? (
                    <LoaderComponent pt={20} />
                ) : (
                    <CustomizedDataGrid
                        title={`Overall Cost (YTD) (${currencyFormatter.format(totalCost)})`}
                        tableColumns={columnsDef}
                        tableData={tableData}
                        tableType="cost-details-ver"
                        defaultColumnNames={defaultColumnNames}
                        page={page}
                        pageSize={pageSize}
                        paginationData={paginationData}
                        listLength={tableData.length}
                        handlepageSizeChange={handlePageSizeChange}
                        handlePageChange={handlePageChange}
                        isDateRangeFilter={false}
                        isDisabled={false}
                        isFetching={loader}
                    />
                )}
            </PageWrapper>
        </OverallCostContext.Provider>
    );
};

export default OverallCostTablePage;